import { useEffect } from 'react'

export interface ShortcutHandlers {
  onUndo: () => void
  onRedo: () => void
  onCopy: () => void
  onCut: () => void
  onPaste: () => void
  onErase: () => void
  onToday: () => void
}

/**
 * Keyboard shortcuts, for an iPad with a Magic Keyboard.
 *
 * Ignored while a text field has focus, so typing a comment does not trigger
 * an erase. `preventDefault` matters for the combinations Safari claims for
 * itself, notably Cmd-F.
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers, enabled = true): void {
  useEffect(() => {
    if (!enabled) return

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return

      const meta = e.metaKey || e.ctrlKey

      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) handlers.onRedo()
        else handlers.onUndo()
        return
      }

      if (meta && e.key.toLowerCase() === 'c') {
        e.preventDefault()
        handlers.onCopy()
        return
      }

      if (meta && e.key.toLowerCase() === 'x') {
        e.preventDefault()
        handlers.onCut()
        return
      }

      if (meta && e.key.toLowerCase() === 'v') {
        e.preventDefault()
        handlers.onPaste()
        return
      }

      if (meta && e.key.toLowerCase() === 't') {
        e.preventDefault()
        handlers.onToday()
        return
      }

      if (!meta && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault()
        handlers.onErase()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handlers, enabled])
}
